import { useState, useRef, useCallback } from "react";

export const useMediaRecorder = (maxDuration = 10000) => {
  const [recording, setRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(maxDuration / 1000);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      chunksRef.current = [];

      // 480p: mobile-friendly (widespread support, smaller files, less CPU). Prefer back camera for scene.
      const constraints480p = {
        video: {
          facingMode: "environment",
          width: { ideal: 854 },
          height: { ideal: 480 },
        },
        audio: true,
      };
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints480p);
      } catch (e) {
        // Fallback: 640x480 (VGA) for older or strict mobile devices
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 640 }, height: { ideal: 480 } },
          audio: true,
        });
      }

      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : MediaRecorder.isTypeSupported("video/webm")
          ? "video/webm"
          : "video/mp4";

      // ~2 MB for a 10 s clip, well under the 4.5 MB upload limit
      const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 1_500_000, audioBitsPerSecond: 64_000 });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setVideoBlob(blob);
        setVideoUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      recorder.start(1000);
      setRecording(true);
      setTimeLeft(maxDuration / 1000);

      let remaining = maxDuration / 1000;
      timerRef.current = setInterval(() => {
        remaining -= 1;
        setTimeLeft(remaining);
        if (remaining <= 0) {
          clearInterval(timerRef.current);
          if (recorder.state === "recording") recorder.stop();
          setRecording(false);
        }
      }, 1000);
    } catch (err) {
      setError("Camera access denied or unavailable.");
    }
  }, [maxDuration]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  }, []);

  const resetRecording = useCallback(() => {
    // Null out state first so any playing <video> src updates before we revoke the blob URL
    const urlToRevoke = videoUrl;
    setVideoBlob(null);
    setVideoUrl(null);
    setTimeLeft(maxDuration / 1000);
    if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
  }, [videoUrl, maxDuration]);

  return {
    recording,
    videoBlob,
    videoUrl,
    error,
    timeLeft,
    startRecording,
    stopRecording,
    resetRecording,
    streamRef,
  };
};
